// MIME allowlists mirroring what the backend nodes actually accept. Several
// formats report more than one MIME depending on browser/OS (a .wav can arrive
// as audio/vnd.wave), so each group lists every variant — a missing variant
// silently filters the file out.
export interface FileTypeGroup {
  id: string;
  label: string;
  mimeTypes: string[];
}

export const FILE_TYPE_GROUPS: FileTypeGroup[] = [
  {
    id: 'audio',
    label: 'Audio — MP3, WAV, M4A',
    mimeTypes: [
      'audio/mpeg',
      'audio/mp3',
      'audio/x-mpeg',
      'audio/wav',
      'audio/x-wav',
      'audio/wave',
      'audio/vnd.wave',
      'audio/mp4',
      'audio/m4a',
      'audio/x-m4a',
    ],
  },
  {
    id: 'documents',
    label: 'Documents — PDF, plain text',
    mimeTypes: ['application/pdf', 'text/plain'],
  },
  {
    id: 'images',
    label: 'Images — JPEG, PNG, GIF, WebP',
    mimeTypes: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
  },
  {
    id: 'video',
    label: 'Video — MP4, MOV, WebM, AVI, MKV',
    mimeTypes: [
      'video/mp4',
      'video/quicktime',
      'video/webm',
      'video/x-msvideo',
      'video/x-matroska',
    ],
  },
];

export const mimeTypesForGroups = (groupIds: string[]): string[] =>
  FILE_TYPE_GROUPS.filter((g) => groupIds.includes(g.id)).flatMap(
    (g) => g.mimeTypes,
  );

export const groupLabelsForMimeTypes = (
  mimeTypes?: string[] | null,
): string[] => {
  if (!mimeTypes?.length) return [];
  return FILE_TYPE_GROUPS.filter((g) =>
    g.mimeTypes.some((m) => mimeTypes.includes(m)),
  ).map((g) => g.label.split(' — ')[0]);
};

// Ceiling tracks the backend's TRIGGER_MATERIALIZE_MAX_BYTES: 10 GB, 1024-based
// like every label below (MB = 1024^2, GB = 1024^3). The backend rejects a filter
// above its ceiling at create time, so an option here that outgrows it fails
// loudly rather than producing triggers that silently never fire.
export const MAX_SIZE_OPTIONS = [
  { label: '100 MB', bytes: 100 * 1024 * 1024 },
  { label: '250 MB', bytes: 250 * 1024 * 1024 },
  { label: '500 MB', bytes: 500 * 1024 * 1024 },
  { label: '1 GB', bytes: 1024 * 1024 * 1024 },
  { label: '2 GB', bytes: 2 * 1024 * 1024 * 1024 },
  { label: '5 GB', bytes: 5 * 1024 * 1024 * 1024 },
  { label: '10 GB', bytes: 10 * 1024 * 1024 * 1024 },
];

// Selected by value, not index: inserting an option earlier in the list must
// not silently change what every new trigger defaults to.
export const DEFAULT_MAX_SIZE_BYTES = 500 * 1024 * 1024;

export const POLL_INTERVAL_OPTIONS = [
  { label: 'Every minute', seconds: 60 },
  { label: 'Every 5 minutes', seconds: 300 },
  { label: 'Every 15 minutes', seconds: 900 },
  { label: 'Every hour', seconds: 3600 },
];
